import { useEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";
import dayjs from "dayjs";
import { useT } from "@app/contexts/preferences.context";
import { Category, Package } from "@app/types";
import { formatMoneyVN } from "@app/utils/util";
import {
  IcCheck,
  IcChevronDown,
  IcChevronLeft,
  IcChevronRight,
  IcClock,
  IcFilter,
  IcSearch,
  IcX,
} from "@app/components/icons";
import { LoadingBlock, OsIcon, PcGlyph, SpecText, btn, btnGhost, btnPrimary, miniBtn, select } from "@app/components/ui";
import { TONE_TAG, categoryTones, toneColor } from "./category-tone";
import { MAX_LEN } from "@app/constants";
import useDragScroll from "@core/hooks/use-drag-scroll.hook";

export const ALL = "__all__";

type SpecKey = "gpu" | "cpu" | "ram" | "ssd";

/** Bỏ nhãn đầu ("SSD: ", "RAM: "…) và khoảng trắng thừa: admin nhập cùng thông số theo nhiều kiểu */
const specLabel = (value?: string) =>
  (value || "")
    .normalize("NFC")
    .replace(/[\u200B-\u200D\uFEFF]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^(cpu|gpu|vga|ram|ssd|hdd)(\s*:\s*|\s+)/i, "");

/** Khoá so sánh của Bộ lọc: thêm không phân biệt hoa thường và "250 GB" = "250GB" */
const specKey = (value?: string) => specLabel(value).toLowerCase().replace(/(\d)\s+(gb|tb)\b/g, "$1$2");

interface ConfigCatalogProps {
  /** Chưa có danh sách cấu hình lần đầu: hiện đang tải thay cho "Không có cấu hình" */
  loading?: boolean;
  /** Danh sách cho chip "Tất cả" */
  items: Package[];
  /** Danh sách theo từng danh mục (key là _id danh mục) */
  itemsByCategory: Record<string, Package[]>;
  categories: Pick<Category, "_id" | "name">[];
  /** discount_pc của tài khoản */
  discount: number;
  selectedId: string;
  onPick: (item: Package | null) => void;
  /** Tab Cloud PC: nhãn loại máy trên thẻ là "Cloud PC" (màu brand như .t-cloud của bản thiết kế) */
  cloudPc?: boolean;
}

function ConfigCatalog({
  loading,
  items,
  itemsByCategory,
  categories,
  discount,
  selectedId,
  onPick,
  cloudPc,
}: ConfigCatalogProps) {
  const t = useT();
  const [cat, setCat] = useState(ALL);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState<"def" | "asc" | "desc">("def");
  const [ready, setReady] = useState(false);
  const [showFilter, setShowFilter] = useState(false);
  const [filter, setFilter] = useState({ gpu: "", cpu: "", ram: "", ssd: "" });
  const [sched, setSched] = useState<string | null>(null);

  // Package lấy từ /packages/categories/:id: nguồn is_available và orders_end_time như code cũ
  // (CreateImageModal / ImageCard / TimeReadyModal), dùng cho cả chip "Tất cả".
  const packageById = useMemo(() => {
    const map: Record<string, Package> = {};

    Object.values(itemsByCategory).forEach((list) => list.forEach((pkg) => (map[pkg._id] = pkg)));

    return map;
  }, [itemsByCategory]);

  // Đóng "Lịch sẵn sàng" bằng phím Esc
  useEffect(() => {
    if (!sched) return;

    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setSched(null);

    document.addEventListener("keydown", onKey);

    return () => document.removeEventListener("keydown", onKey);
  }, [sched]);
  const [edges, setEdges] = useState({ start: true, end: true });

  const gridRef = useRef<HTMLDivElement>(null);
  const chipsRef = useDragScroll<HTMLDivElement>();

  const price = (x: Package) => x.price_per_hour - (x.price_per_hour * discount) / 100;

  /** Danh mục của thẻ theo danh sách package của từng danh mục */
  const categoryOf = (x: Package) => categories.find((c) => itemsByCategory[c._id]?.some((p) => p._id === x._id));

  // Mỗi danh mục 1 màu: chữ danh mục, khung và đèn vỏ case, nền ảnh và chấm thông số
  const tones = useMemo(() => categoryTones(categories), [categories]);

  const list = useMemo(() => {
    const source = cat === ALL ? items : itemsByCategory[cat] || [];
    const needle = q.trim().toLowerCase();
    const rows = source.filter(
      (x) =>
        (!ready || (packageById[x._id] || x).is_available) &&
        (Object.keys(filter) as SpecKey[]).every((k) => !filter[k] || specKey(x[k]) === filter[k]) &&
        (!needle ||
          [x.name, x.cpu, x.gpu, x.ram, x.ssd, x.os]
            .join(" ")
            .toLowerCase()
            .includes(needle))
    );

    // "Phổ biến" giữ thứ tự API trả về (sort_order admin nhập, tăng dần); sort theo giá ổn định nên cùng giá vẫn giữ thứ tự đó
    if (sort === "def") return rows;

    return [...rows].sort((a, b) => (sort === "asc" ? price(a) - price(b) : price(b) - price(a)));
  }, [items, itemsByCategory, packageById, cat, q, ready, filter, sort, discount]);

  // Các lựa chọn của Bộ lọc lấy từ chính danh sách cấu hình. Gộp các giá trị chỉ khác khoảng trắng / hoa thường / nhãn đầu
  // (trước đây "SSD: 1000GB  Nvme" và "SSD: 1000GB Nvme" là 2 lựa chọn, mỗi cái ra 1 phần kết quả).
  const filterOptions = useMemo(() => {
    const options = (k: SpecKey) => {
      // khoá -> các cách viết và số lần gặp; chữ hiện là cách viết gặp nhiều nhất
      const groups = new Map<string, Map<string, number>>();

      items.forEach((x) => {
        const key = specKey(x[k]);

        if (!key) return;

        const labels = groups.get(key) || new Map<string, number>();
        const label = specLabel(x[k]);

        labels.set(label, (labels.get(label) || 0) + 1);
        groups.set(key, labels);
      });

      return Array.from(groups, ([value, labels]) => ({
        value,
        label: Array.from(labels).sort((a, b) => b[1] - a[1])[0][0],
      })).sort((a, b) => a.label.localeCompare(b.label, "vi", { numeric: true, sensitivity: "base" }));
    };

    return { gpu: options("gpu"), cpu: options("cpu"), ram: options("ram"), ssd: options("ssd") };
  }, [items]);

  const filterCount = Object.values(filter).filter(Boolean).length;

  const updateEdges = () => {
    const g = gridRef.current;

    if (g) {
      setEdges({ start: g.scrollLeft < 4, end: g.scrollLeft + g.clientWidth >= g.scrollWidth - 4 });
    }
  };

  // Đổi bộ lọc thì cuộn về đầu. Dữ liệu tự cập nhật mỗi 5s (Sẵn sàng / Hết máy) thì giữ nguyên vị trí cuộn.
  useEffect(() => {
    gridRef.current?.scrollTo({ left: 0 });
  }, [cat, q, ready, filter, sort]);

  useEffect(() => {
    updateEdges();
  }, [list]);

  const slide = (dir: number) => {
    const g = gridRef.current;

    g?.scrollBy({ left: dir * g.clientWidth, behavior: "smooth" });
  };

  const chip = (active: boolean) =>
    clsx(
      "inline-flex flex-none items-baseline gap-1.5 rounded-full border px-3.5 py-1.5 text-[13px] font-medium leading-5",
      active ? "border-fg bg-fg text-surface" : "border-line bg-surface text-muted hover:text-fg"
    );

  const arrow =
    "absolute top-1/2 z-[3] grid h-8 w-8 -translate-y-1/2 place-items-center rounded-full border border-line bg-surface text-brand shadow-card hover:bg-brand hover:text-white max-[760px]:hidden";

  return (
    // khung Thông tin tạo máy cao theo cột này (.sum-abs) nên 2 cột thẳng cạnh dưới như bản thiết kế
    <section className="flex min-w-0 flex-col gap-3.5">
      {/* tìm kiếm + sắp xếp */}
      <div className="flex flex-wrap items-center gap-2.5">
        <label className="flex h-10 flex-[1_1_260px] items-center gap-2 rounded-[10px] border border-line bg-surface px-3 text-muted focus-within:border-brand">
          <IcSearch className="h-[18px] w-[18px] flex-none" />
          <input
            type="search"
            maxLength={MAX_LEN.SEARCH}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("Tìm theo tên, CPU, GPU…")}
            className="min-w-0 flex-1 border-0 bg-transparent text-fg outline-none"
          />
        </label>
        <button
          type="button"
          className={clsx(btn, btnGhost, "!h-10", showFilter && "!border-brand !bg-brand-soft !text-brand")}
          onClick={() => setShowFilter(!showFilter)}
        >
          <IcFilter className="h-[18px] w-[18px]" />
          {t("Bộ lọc")}
          {filterCount > 0 && (
            <span className="rounded-[10px] bg-brand px-1.5 text-[11px] leading-[18px] text-brand-ink">{filterCount}</span>
          )}
        </button>
        {/* <= 420px ô sắp xếp giãn hết phần còn lại như bản thiết kế */}
        <select className={clsx(select, "max-[420px]:flex-1")} value={sort} onChange={(e) => setSort(e.target.value as typeof sort)}>
          <option value="def">{t("Phổ biến")}</option>
          <option value="asc">{t("Giá thấp → cao")}</option>
          <option value="desc">{t("Giá cao → thấp")}</option>
        </select>
      </div>

      {showFilter && (
        <div className="grid grid-cols-[repeat(4,minmax(0,1fr))_auto] items-end gap-2.5 rounded-card border border-line bg-surface p-3.5 max-[1240px]:grid-cols-2 max-[420px]:grid-cols-1">
          {(
            [
              ["gpu", "Dòng GPU"],
              ["cpu", "CPU"],
              ["ram", "Dung lượng RAM"],
              ["ssd", "Dung lượng ổ"],
            ] as [keyof typeof filter, string][]
          ).map(([key, label]) => (
            <label key={key} className="flex flex-col gap-1 text-xs font-semibold text-muted">
              {t(label)}
              <select
                className={clsx(select, "w-full")}
                value={filter[key]}
                onChange={(e) => setFilter({ ...filter, [key]: e.target.value })}
              >
                <option value="">{t("Tất cả")}</option>
                {filterOptions[key].map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
          ))}
          <button
            type="button"
            className={clsx(btn, btnGhost, "!h-10")}
            onClick={() => setFilter({ gpu: "", cpu: "", ram: "", ssd: "" })}
          >
            {t("Xoá lọc")}
          </button>
        </div>
      )}

      {/* danh mục: nhiều thì cuộn ngang, có thanh cuộn mảnh và kéo được bằng chuột */}
      <div ref={chipsRef} className="thin-scroll flex gap-2 overflow-x-auto pb-1.5">
        <button type="button" className={chip(cat === ALL)} onClick={() => setCat(ALL)}>
          {t("Tất cả")} <em className="text-xs not-italic leading-5 opacity-70">{items.length}</em>
        </button>
        {/* như bản thiết kế: danh mục không có cấu hình nào ở tab này thì không hiện */}
        {categories.filter((c) => itemsByCategory[c._id]?.length).map((c) => (
          <button key={c._id} type="button" className={chip(cat === c._id)} onClick={() => setCat(c._id)}>
            {c.name}{" "}
            <em className="text-xs not-italic leading-5 opacity-70">
              {itemsByCategory[c._id]?.length || 0}
            </em>
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2.5 text-[13px] text-muted">
        <span>
          {t("Có")} <b className="text-fg">{list.length}</b> {t("cấu hình")}
        </span>
        <label className="inline-flex cursor-pointer select-none items-center gap-2">
          <input type="checkbox" className="peer sr-only" checked={ready} onChange={(e) => setReady(e.target.checked)} />
          <span className="relative h-5 w-[34px] rounded-[10px] bg-line-2 transition-colors after:absolute after:left-0.5 after:top-0.5 after:h-4 after:w-4 after:rounded-full after:bg-white after:transition-transform peer-checked:bg-brand peer-checked:after:translate-x-3.5" />
          {t("Chỉ hiện máy sẵn sàng")}
        </label>
      </div>

      {loading && !items.length ? (
        <LoadingBlock className="rounded-card border border-line bg-surface" />
      ) : !list.length ? (
        // nằm ngoài lưới cuộn ngang (grid-flow-col) để không bị bóp thành 1 cột hẹp
        <div className="flex flex-col items-center gap-2 rounded-card border border-dashed border-line-2 px-4 py-10 text-center text-muted">
          {t("Không có cấu hình nào khớp bộ lọc.")}
        </div>
      ) : null}

      {/* 1 hàng thẻ, bấm mũi tên để xem tiếp */}
      <div className={clsx("relative flex flex-1 flex-col", !list.length && "hidden")}>
        {!edges.start && (
          <button type="button" className={clsx(arrow, "-left-5")} onClick={() => slide(-1)}>
            <IcChevronLeft className="h-[18px] w-[18px]" />
          </button>
        )}
        <div
          ref={gridRef}
          onScroll={updateEdges}
          className="no-scrollbar -mx-[3px] -mb-2 -mt-1 grid flex-1 snap-x snap-mandatory grid-flow-col gap-3 overflow-x-auto px-[3px] pb-2 pt-1 [--per:5] [grid-auto-columns:calc((100%_-_(var(--per)_-_1)*12px)/var(--per))] min-[1101px]:max-[1439px]:[--per:4] max-[760px]:[--per:3] max-[560px]:[--per:2.15]"
        >

          {list.map((x) => {
            const source = packageById[x._id] || x;
            const available = source.is_available;
            const gpu = x.gpu;
            const on = selectedId === x._id;
            const scheduleOpen = !available && sched === x._id;
            // 3 thời điểm có máy gần nhất (thời điểm kết thúc của các đơn đang thuê)
            const readyTimes = [...(source.orders_end_time || [])]
              .sort((a, b) => dayjs(a).valueOf() - dayjs(b).valueOf())
              .slice(0, 3);
            const category = categoryOf(x);
            const tone = category ? tones[category._id] : undefined;
            const glow = toneColor(tone);

            return (
              <article
                key={x._id}
                onClick={() => available && onPick(on ? null : x)}
                style={{ ["--glow" as string]: glow }}
                className={clsx(
                  "relative flex snap-start flex-col overflow-hidden rounded-card border bg-surface transition-shadow",
                  available ? "cursor-pointer hover:shadow-card" : "cursor-default",
                  on ? "border-brand shadow-[0_0_0_3px_var(--brand-soft)]" : "border-line"
                )}
              >
                <div className="relative grid h-[100px] place-items-center pc-media">
                  {/* Hết máy: ảnh xám như bản thiết kế */}
                  <span className={clsx("grid place-items-center", !available && "opacity-[.55] grayscale")}>
                    <PcGlyph color={glow} />
                  </span>
                  <span
                    className={clsx(
                      "absolute left-2 top-2 rounded-full px-2 py-0.5 text-[11px] font-semibold",
                      available ? "bg-ok-soft text-ok" : "bg-bad-soft text-bad"
                    )}
                  >
                    ● {available ? t("Sẵn sàng") : t("Hết máy")}
                  </span>
                  {on && (
                    <span className="absolute right-2 top-2 grid h-6 w-6 place-items-center rounded-full bg-brand text-white">
                      <IcCheck className="h-3.5 w-3.5" />
                    </span>
                  )}
                </div>

                <div className="flex flex-1 flex-col gap-2 p-[11px]">
                  <h3 className="text-[14.5px] font-bold leading-tight">{x.name}</h3>
                  <div className="flex flex-wrap gap-1 text-[11px] font-semibold">
                    {category && (
                      <span className={clsx("rounded-md px-[7px] py-px", tone && TONE_TAG[tone])}>{category.name}</span>
                    )}
                    <span
                      className={clsx(
                        "rounded-md px-[7px] py-px",
                        cloudPc ? "bg-brand-soft text-brand" : "bg-surface-2 text-muted"
                      )}
                    >
                      {cloudPc ? "Cloud PC" : t("Máy vật lý")}
                    </span>
                  </div>
                  <ul className="m-0 grid list-disc gap-0.5 pl-4 text-[12.5px] font-medium marker:text-[color:var(--glow)]">
                    {/* thông số có dấu "/": phần sau xuống dòng, chữ nhỏ (SpecText) */}
                    <li>
                      <SpecText value={x.cpu} />
                    </li>
                    <li className={clsx(!gpu && "italic text-muted")}>{gpu ? <SpecText value={gpu} /> : t("Không có VGA")}</li>
                    <li>
                      <SpecText value={x.ram} />
                    </li>
                    <li>
                      <SpecText value={x.ssd} />
                    </li>
                  </ul>
                  {/* hệ điều hành admin nhập ở Package Management */}
                  {x.os && (
                    <span
                      title={x.os}
                      className="flex max-w-full items-start gap-1.5 self-start rounded-md bg-surface-2 px-[7px] py-[3px] text-[11.5px] font-semibold leading-[15px] text-muted"
                    >
                      <OsIcon os={x.os} className="mt-px h-3 w-3 text-[color:var(--glow)]" />
                      {/* thẻ hẹp thì xuống dòng, tối đa 2 dòng */}
                      <span className="line-clamp-2 min-w-0 [overflow-wrap:anywhere]">{x.os}</span>
                    </span>
                  )}

                  <div className="mt-auto border-t border-dashed border-line pt-2 tabular-nums">
                    <div className="flex items-baseline gap-1">
                      <strong className={clsx("text-[19px] font-bold", available ? "text-brand" : "text-muted")}>
                        {formatMoneyVN(price(x))}
                      </strong>
                      <span className="text-[12.5px] text-muted">{`VND/${t("giờ")}`}</span>
                    </div>
                    <div className="text-[11.5px] leading-[1.45] text-muted">
                      {formatMoneyVN(x.price_per_day - (x.price_per_day * discount) / 100)} VND/24h
                      <br />
                      {formatMoneyVN(x.price_per_month - (x.price_per_month * discount) / 100)} VND/720h
                    </div>
                  </div>

                  {available ? (
                    <button type="button" className={clsx(btn, "w-full", on ? "bg-brand-soft text-brand" : btnPrimary)}>
                      {on ? t("Đã chọn") : t("Chọn cấu hình")}
                    </button>
                  ) : (
                    <button
                      type="button"
                      aria-expanded={scheduleOpen}
                      className={clsx(btn, btnGhost, "w-full", scheduleOpen && "!border-bad !text-bad")}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSched(scheduleOpen ? null : x._id);
                      }}
                    >
                      <IcClock className="h-4 w-4" />
                      {t("Lịch sẵn sàng")}
                      <IcChevronDown className={clsx("h-3.5 w-3.5 transition-transform", scheduleOpen && "rotate-180")} />
                    </button>
                  )}
                </div>

                {/* Lịch sẵn sàng: thay cho nút "Check Time Ready" / TimeReadyModal cũ */}
                {scheduleOpen && (
                  <div
                    role="dialog"
                    onClick={(e) => e.stopPropagation()}
                    className="absolute bottom-[58px] left-1.5 right-1.5 z-[2] flex flex-col gap-1.5 rounded-[10px] border border-line-2 bg-surface p-2 shadow-[0_8px_24px_rgba(13,24,48,.18)]"
                  >
                    <div className="flex items-center justify-between pl-0.5 text-[12.5px] font-semibold">
                      {t("Dự kiến có máy")}
                      <button type="button" className={clsx(miniBtn, "!h-[22px] !w-[22px]")} aria-label={t("Đóng")} onClick={() => setSched(null)}>
                        <IcX className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    {readyTimes.length ? (
                      readyTimes.map((time, index) => (
                        <div
                          key={time + index}
                          className="flex items-center gap-[7px] rounded-[7px] bg-bad-soft px-2 py-[5px] text-xs leading-tight"
                        >
                          <IcClock className="h-3.5 w-3.5 flex-none text-bad" />
                          <div>
                            <b className="block font-semibold">
                              {t("Đợt")} {index + 1}: {dayjs(time).format("HH:mm")}
                            </b>
                            <span className="text-[11.5px] tabular-nums text-muted">{dayjs(time).format("DD/MM/YYYY")}</span>
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="m-0 px-1 text-xs text-muted">{t("Chưa có lịch dự kiến.")}</p>
                    )}
                    <p className="m-0 px-1 text-[11px] text-faint">{t("Lưu ý: time ready có thể lệch từ 1-5 phút")}</p>
                  </div>
                )}
              </article>
            );
          })}
        </div>
        {!edges.end && (
          <button type="button" className={clsx(arrow, "-right-5")} onClick={() => slide(1)}>
            <IcChevronRight className="h-[18px] w-[18px]" />
          </button>
        )}
      </div>
    </section>
  );
}

export default ConfigCatalog;
